import React, { useState, useEffect, useRef, useCallback } from 'react';
import { PART2_TOPICS } from './TrainingContainer';
import { useLanguage } from '../contexts/LanguageContext';
import { isIPadSafari } from '../utils/deviceDetection';
import { forceStopIPadRecording } from '../utils/speechUtils';
import TipButton from './TipButton';

const Part2Training = ({ 
  username, 
  setShowCompletionModal, 
  setCurrentMessages,
  onRepeatTopic,
  onNewRound,
  onEndTraining
}) => {
  // 添加语言上下文
  const { t, language } = useLanguage();
  
  // 练习相关状态
  const [prepTime, setPrepTime] = useState(60); // Part 2准备时间为60秒
  const [practiceTime, setPracticeTime] = useState(90); // Part 2回答时间为90秒
  const [timer, setTimer] = useState(prepTime);
  const [gamePhase, setGamePhase] = useState('idle');
  
  // Part 2特有状态
  // 初始化为null，将在useEffect中随机选择
  const [currentPart2Topic, setCurrentPart2Topic] = useState(null);
  
  // 录音相关状态
  const [isRecording, setIsRecording] = useState(false);
  const [transcription, setTranscription] = useState('');
  const recognitionRef = useRef(null);
  const isRecordingRef = useRef(false);
  const fullTranscriptionRef = useRef('');

  // 对话消息状态 - 初始为空数组
  const [part2Messages, setPart2Messages] = useState([]);
  
  // 创建聊天消息容器的引用
  const messagesEndRef = useRef(null);

  // 使用useCallback包装startSpeechRecognition，确保它的引用稳定性
  const startSpeechRecognition = useCallback(() => {
    console.log('开始初始化语音识别...');
    
    // 检查浏览器支持
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      console.error('浏览器不支持语音识别');
      alert('您的浏览器不支持语音识别功能，请使用Chrome浏览器');
      return;
    }

    try {
      // 创建语音识别实例
      console.log('创建语音识别实例...');
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      // 清空先前的转录
      fullTranscriptionRef.current = '';
      setTranscription('');

      // 处理结果
      recognitionRef.current.onresult = (event) => {
        let interimTranscript = '';
        let finalTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
            console.log('识别到最终转录片段:', transcript);
          } else {
            interimTranscript += transcript;
            console.log('识别到临时转录:', transcript);
          }
        }

        // 更新全局转录和显示转录
        if (finalTranscript) {
          fullTranscriptionRef.current += finalTranscript;
          console.log('更新全局转录:', fullTranscriptionRef.current);
        }
        
        setTranscription(fullTranscriptionRef.current + interimTranscript);
      };

      // 处理错误
      recognitionRef.current.onerror = (event) => {
        console.error('语音识别错误:', event.error);
        
        // 如果是iPad Safari上的错误，不显示错误提示，因为我们使用了替代方案
        if (!isIPadSafari()) {
          if (event.error === 'no-speech') {
            console.log('未检测到语音');
          } else {
            alert(`语音识别错误: ${event.error}`);
          }
        }
        
        // 对于"no-speech"错误，我们不停止录音，让用户继续尝试
        if (event.error !== 'no-speech') {
          setIsRecording(false);
        }
      };

      // 处理结束
      recognitionRef.current.onend = () => {
        console.log('语音识别结束');
        
        // 在iPad Safari上，我们使用的是替代方案，不需要在这里重新启动
        if (!isIPadSafari() && isRecording) {
          // 如果仍在录音状态，自动重新开始录音
          try {
            console.log('重新启动语音识别');
            recognitionRef.current.start();
          } catch (e) {
            console.error('重新启动语音识别失败:', e);
          }
        }
      };

      // 开始识别
      recognitionRef.current.start();
      setIsRecording(true);
      console.log('语音识别已启动');
    } catch (e) {
      console.error('启动语音识别失败:', e);
      alert('启动语音识别失败，请刷新页面重试');
    }
  }, [isRecording]);

  // 使用useCallback包装stopSpeechRecognition，确保它的引用稳定性
  const stopSpeechRecognition = useCallback(() => {
    if (recognitionRef.current) {
      // 防止重复调用
      if (!isRecordingRef.current) {
        console.log('录音已经停止，跳过处理');
        return;
      }
      
      console.log('开始停止语音识别，记录当前的转录内容');
      // 保存当前转录内容，防止在停止过程中丢失
      const currentTranscriptionContent = fullTranscriptionRef.current.trim();
      console.log('保存当前转录内容:', currentTranscriptionContent);
      
      // 标记录音状态，但延迟真正停止语音识别，给浏览器时间处理任何最后的识别结果
      isRecordingRef.current = false;
      setIsRecording(false);

      // 延迟停止，确保捕获最后的内容
      setTimeout(() => {
        try {
          // 再次检查最终内容，可能在延迟期间有新内容添加
          const finalTranscriptionContent = fullTranscriptionRef.current.trim() || currentTranscriptionContent;
          console.log('停止前的最终转录内容:', finalTranscriptionContent);
          
          // 停止语音识别
          recognitionRef.current.stop();
          console.log('语音识别已停止');

          // 使用最终保存的转录内容
          const contentToSend = finalTranscriptionContent || t('no_speech_content');
          console.log('最终发送到对话框的内容:', contentToSend);
          
          const userMessage = {
            type: 'user',
            content: contentToSend,
            time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
          };
          
          setPart2Messages(prev => {
            const newMessages = [...prev, userMessage];
            // Send messages to container for potential download
            console.log('Part2Training: 更新消息列表，总数:', newMessages.length);
            console.log('Part2Training: 消息内容:', newMessages);
            setCurrentMessages(newMessages);
            return newMessages;
          });
        } catch (error) {
          console.error('停止录音失败:', error);
        }
      }, 300); // 添加300毫秒延迟，给浏览器时间处理最后的识别结果
    }
  }, [t, setCurrentMessages]);

  // 组件初始化时随机选择一个话题
  useEffect(() => {
    // 只有在初次挂载时（currentPart2Topic为null时）才随机选择话题
    if (!currentPart2Topic) {
      // 每次组件挂载（用户切换到Part2时）都随机选择一个新话题
      const randomIndex = Math.floor(Math.random() * PART2_TOPICS.length);
      const selectedTopic = PART2_TOPICS[randomIndex];
      setCurrentPart2Topic(selectedTopic);
      
      // 设置欢迎消息和随机选择的话题
      const welcomeMessage = {
        type: 'mentor',
        content: 'Welcome to IELTS Speaking Part 2 practice. Please select a topic card to begin.',
        time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
      };
      
      setPart2Messages([welcomeMessage]);
      setCurrentMessages([welcomeMessage]);
    }
    
    // 清理函数 - 组件卸载时重置状态
    return () => {
      // 重置状态，确保下次挂载时是全新的状态
      setGamePhase('idle');
      setTimer(prepTime);
      setTranscription('');
      if (isRecordingRef.current) {
        stopSpeechRecognition();
      }
    };
  }, [currentPart2Topic, stopSpeechRecognition, setCurrentMessages]); // 从依赖中移除prepTime，避免调整时间触发话题重选

  // 当消息更新时自动滚动到底部
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [part2Messages]);

  // 确保在part2Messages变化时总是更新currentMessages
  useEffect(() => {
    if (part2Messages.length > 0) {
      setCurrentMessages(part2Messages);
    }
  }, [part2Messages, setCurrentMessages]);

  // 处理倒计时
  useEffect(() => {
    if (gamePhase === 'idle' || gamePhase === 'completed') {
      return;
    }

    const countdown = setInterval(() => {
      setTimer(prevTimer => {
        if (prevTimer <= 1) {
          clearInterval(countdown);
          
          if (gamePhase === 'prep') {
            console.log('准备阶段结束，开始练习阶段');
            setGamePhase('practice');
            setTimer(practiceTime);
            
            // 开始自动录音
            console.log('准备启动语音识别');
            // 确保在状态更新后启动语音识别
            setTimeout(() => {
              startSpeechRecognition();
            }, 100);
          } else if (gamePhase === 'practice') {
            console.log('练习阶段结束，当前转录内容:', fullTranscriptionRef.current);
            
            // 先停止录音并保存转录内容
            if (isRecording && recognitionRef.current) {
              console.log('准备停止语音识别');
              isRecordingRef.current = false;
              setIsRecording(false);
              
              // 针对iPad设备的特殊处理
              if (isIPadSafari()) {
                console.log('检测到iPad设备，使用强制停止方法');
                recognitionRef.current.stop();
                
                // 获取当前转录内容
                const contentToSend = fullTranscriptionRef.current.trim() || t('no_speech_content');
                console.log('iPad设备，保存转录内容:', contentToSend);
                
                const userMessage = {
                  type: 'user',
                  content: contentToSend,
                  time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
                };
                
                setPart2Messages(prev => {
                  const newMessages = [...prev, userMessage];
                  console.log('Part2: 练习结束添加用户消息，消息总数:', newMessages.length);
                  setCurrentMessages(newMessages);
                  return newMessages;
                });
              } else {
                // 非iPad设备
                recognitionRef.current.stop();
                console.log('非iPad设备，停止语音识别');
                
                // 获取当前转录内容
                const contentToSend = fullTranscriptionRef.current.trim() || t('no_speech_content');
                console.log('保存转录内容:', contentToSend);
                
                const userMessage = {
                  type: 'user',
                  content: contentToSend,
                  time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
                };
                
                setPart2Messages(prev => {
                  const newMessages = [...prev, userMessage];
                  console.log('Part2: 练习结束添加用户消息，消息总数:', newMessages.length);
                  setCurrentMessages(newMessages);
                  return newMessages;
                });
              }
              
              // 延迟更长时间确保状态完全更新
              setTimeout(() => {
                console.log('练习完成，设置状态为completed');
                setGamePhase('completed');
                
                // 再等待一段时间确保消息完全同步
                setTimeout(() => {
                  console.log('显示完成模态框');
                  setShowCompletionModal(true);
                }, 1500);
              }, 1000);
            } else {
              // 没有录音，直接完成
              setGamePhase('completed');
              setTimeout(() => setShowCompletionModal(true), 500);
            }
            return 0;
          }
          
          return 0;
        }
        return prevTimer - 1;
      });
    }, 1000);

    // 清理函数
    return () => {
      clearInterval(countdown);
      // 如果组件卸载时正在录音，确保停止录音
      if (isRecording) {
        console.log('组件卸载，停止录音');
        if (isIPadSafari()) {
          forceStopIPadRecording();
        } else {
          stopSpeechRecognition();
        }
      }
    };
  }, [gamePhase, practiceTime, isRecording, stopSpeechRecognition, setShowCompletionModal, startSpeechRecognition, t, setCurrentMessages]);

  // 调整时间的处理函数
  const adjustPrepTime = (amount) => {
    setPrepTime(prevTime => {
      const newTime = Math.max(0, prevTime + amount);
      // 如果在准备阶段，同时更新当前计时器
      if (gamePhase === 'prep') {
        setTimer(newTime);
      }
      return newTime;
    });
  };
  
  const adjustPracticeTime = (amount) => {
    setPracticeTime(prevTime => {
      const newTime = Math.max(0, prevTime + amount);
      // 如果在练习阶段，同时更新当前计时器
      if (gamePhase === 'practice') {
        setTimer(newTime);
      }
      return newTime;
    });
  };

  // 处理话题选择
  const handlePart2TopicSelected = (topic, userMessage) => {
    setPart2Messages(prev => {
      let newMessages = [...prev];
      
      if (topic) {
        const mentorMessage = {
          type: 'mentor',
          content: `${t('discuss_topic')}${topic}`,
          time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
        };
        newMessages.push(mentorMessage);
      }

      if (userMessage) {
        // 如果是语音消息，使用特殊的展示方式
        if (userMessage.isVoice) {
          // 创建语音消息对象
          const voiceMessage = {
            type: userMessage.type,
            isVoice: true,
            content: userMessage.content, // 保存原始内容以便再次播放
            displayContent: t('voice_message'), // 在聊天框中显示的内容
            time: userMessage.time || new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' }),
            controls: userMessage.controls // 保留控制属性
          };
          newMessages.push(voiceMessage);
        } else {
          newMessages.push(userMessage);
        }
      }
      
      // 同步更新容器的消息
      console.log('Part2: handlePart2TopicSelected 更新消息，总数:', newMessages.length);
      setCurrentMessages(newMessages);
      return newMessages;
    });
  };

  // 抽取新Part 2话题
  const getNewPart2Topic = useCallback(() => {
    const randomIndex = Math.floor(Math.random() * PART2_TOPICS.length);
    const newTopic = PART2_TOPICS[randomIndex];
    setCurrentPart2Topic(newTopic);
    
    // 添加新话题消息到对话 - 只发送话题卡片原文
    const fullTopicText = `${newTopic.instruction}\n\n• ${newTopic.prompts.join('\n• ')}`;
    
    const mentorMessage = {
      type: 'mentor',
      content: fullTopicText,
      time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
    };
    setPart2Messages(prev => {
      const newMessages = [...prev, mentorMessage];
      console.log('Part2: getNewPart2Topic 更新消息，总数:', newMessages.length);
      setCurrentMessages(newMessages);
      return newMessages;
    });
  }, [setCurrentMessages]);

  // 开始Part 2练习
  const startPart2Practice = () => {
    // 添加话题卡片到消息列表
    if (currentPart2Topic) {
      const fullTopicText = `${currentPart2Topic.instruction}\n\n• ${currentPart2Topic.prompts.join('\n• ')}`;
      
      const topicMessage = {
        type: 'mentor',
        content: fullTopicText,
        time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
      };
      
      setPart2Messages(prev => {
        const newMessages = [...prev, topicMessage];
        console.log('Part2: startPart2Practice 添加话题，消息总数:', newMessages.length);
        setCurrentMessages(newMessages);
        return newMessages;
      });
    }
    
    // 先设置准备阶段
    setGamePhase('prep');
    setTimer(prepTime);
  };

  // 添加播放语音消息的函数
  const playVoiceMessage = (message) => {
    if (message.isVoice && message.content) {
      console.log('尝试播放语音消息:', message.content);
      
      // 检查浏览器支持
      if (!window.speechSynthesis) {
        console.error('浏览器不支持语音合成');
        alert('您的浏览器不支持语音合成功能，请使用Chrome浏览器');
        return;
      }
      
      // 使用Web Speech API播放语音
      try {
      const utterance = new SpeechSynthesisUtterance(message.content);
      utterance.lang = 'en-US'; // 英语发音
      utterance.rate = 0.9; // 语速稍慢一些，更清晰
        
      window.speechSynthesis.cancel(); // 取消当前正在播放的语音
      window.speechSynthesis.speak(utterance);
        
        console.log('语音播放已启动');
      } catch (error) {
        console.error('语音合成错误:', error);
        alert('语音播放失败，请刷新页面重试');
      }
    }
  };

  // 添加显示原文的函数
  const showOriginalText = (message) => {
    // 显示原文的实现
  };

  // Add handlers for modal actions
  const handleRepeatTopic = () => {
    // Reset messages but keep current topic
    setPart2Messages([{
      type: 'mentor',
      content: 'Welcome to IELTS Speaking Part 2 practice. Please select a topic card to begin.',
      time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
    }]);
    
    // Add current topic message to dialog
    const fullTopicText = `${currentPart2Topic.instruction}\n\n• ${currentPart2Topic.prompts.join('\n• ')}`;
    
    const mentorMessage = {
      type: 'mentor',
      content: fullTopicText,
      time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
    };
    setPart2Messages(prev => [...prev, mentorMessage]);
    
    setTranscription('');
    setGamePhase('idle');
    setTimer(practiceTime);
    
    // Call parent handler if provided
    if (onRepeatTopic) onRepeatTopic();
  };
  
  const handleNewRound = () => {
    // Reset messages
    setPart2Messages([{
      type: 'mentor',
      content: 'Welcome to IELTS Speaking Part 2 practice. Please select a topic card to begin.',
      time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
    }]);
    
    setTranscription('');
    setGamePhase('idle');
    setTimer(practiceTime);
    
    // Get a new topic
    getNewPart2Topic();
    
    // Call parent handler if provided
    if (onNewRound) onNewRound();
  };

  // Add resetTrainingState useEffect after stopSpeechRecognition
  useEffect(() => {
    const resetTrainingState = (event) => {
      // 确保只有当前组件接收到针对它的重置事件
      if (event.detail.part === 'part2') {
        // 重置消息
        setPart2Messages([{
          type: 'mentor',
          content: 'Welcome to IELTS Speaking Part 2 practice. Please select a topic card to begin.',
          time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
        }]);
        
        // 重置录音和转录状态
        setTranscription('');
        if (isRecordingRef.current) {
          stopSpeechRecognition();
        }
        setGamePhase('idle');
        setTimer(practiceTime);
        
        // 获取新话题
        getNewPart2Topic();
      }
    };
    
    // 添加事件监听器
    window.addEventListener('resetTrainingState', resetTrainingState);
    
    // 清理函数
    return () => {
      window.removeEventListener('resetTrainingState', resetTrainingState);
    };
  }, [practiceTime, stopSpeechRecognition, getNewPart2Topic]); // 添加stopSpeechRecognition和getNewPart2Topic到依赖

  // 添加对话完成后的重置函数
  useEffect(() => {
    // 监听完成弹窗关闭事件
    const handleCompletionModalClosed = (event) => {
      if (event.detail && event.detail.part === 'part2') {
        console.log("检测到Part2训练完成弹窗关闭事件，重置训练状态");
        // 重置状态到初始状态
        setPart2Messages([{
          type: 'mentor',
          content: 'Welcome to IELTS Speaking Part 2 practice. Please select a topic card to begin.',
          time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
        }]);
        
        // 重置录音状态
        if (isRecordingRef.current) {
          try {
            stopSpeechRecognition();
          } catch (e) {
            console.error("停止语音识别失败:", e);
          }
        }
        
        // 重置游戏状态
        setGamePhase('idle');
        setTranscription('');
        setTimer(practiceTime);
      }
    };

    // 添加事件监听
    window.addEventListener('completionModalClosed', handleCompletionModalClosed);

    // 清理函数
    return () => {
      window.removeEventListener('completionModalClosed', handleCompletionModalClosed);
    };
  }, [practiceTime, stopSpeechRecognition]); // 添加stopSpeechRecognition到依赖

  return (
    <>
      <TipButton content={t('part2_preparation_note')} language={language} />
      <div className="part-content">
        <div className="part2-content-area">
          <div className="part2-header">
            <h2>{t('part2_title')}</h2>
            <p>{t('part2_description')}</p>
          </div>
          
          <div className="part2-card-container">
            <div className="part2-topic-card">
              <div className="card-header">
                <span className="card-title">{t('topic_card')}</span>
                <button 
                  className="get-topic-btn" 
                  onClick={getNewPart2Topic} 
                  disabled={gamePhase !== 'idle'}
                >
                  {t('get_new_topic')}
                </button>
              </div>
              <div className="card-content">
                {currentPart2Topic && (
                  <>
                <p className="card-instruction">{currentPart2Topic.instruction}</p>
                <ul className="card-prompts">
                  {currentPart2Topic.prompts.map((prompt, index) => (
                    <li key={index}>{prompt}</li>
                  ))}
                </ul>
                  </>
                )}
              </div>
            </div>
            
            <div className="part2-timer">
              <div className="card-header" style={{ marginBottom: '1rem' }}>
                <span className="card-title">{t('preparation_time')}</span>
              </div>
              <div>
                <div className="timer-section">
                  <div className="timer-label">{t('preparation_time')}</div>
                  <div className="timer-controls">
                    <button className="timer-btn" onClick={() => adjustPrepTime(-5)}>-</button>
                    <span className="timer-value">{prepTime}{t('seconds')}</span>
                    <button className="timer-btn" onClick={() => adjustPrepTime(5)}>+</button>
                  </div>
                </div>
                
                <div className="timer-section" style={{ marginTop: '1.5rem' }}>
                  <div className="timer-label">{t('speaking_time')}</div>
                  <div className="timer-controls">
                    <button className="timer-btn" onClick={() => adjustPracticeTime(-5)}>-</button>
                    <span className="timer-value">{practiceTime}{t('seconds')}</span>
                    <button className="timer-btn" onClick={() => adjustPracticeTime(5)}>+</button>
                  </div>
                </div>
              </div>
              
              <button 
                className="start-practice-btn" 
                onClick={startPart2Practice}
                disabled={gamePhase !== 'idle'}
              >
                {gamePhase === 'idle' ? t('start_practice') : t('practicing')}
              </button>
            </div>
            
            {/* 添加倒计时和阶段指示区域 */}
            {gamePhase !== 'idle' && (
              <div className="part2-countdown-container">
                <div className="part2-countdown">
                  <div className="phase-indicator">
                    {gamePhase === 'prep' ? t('preparation_phase_part1') : gamePhase === 'practice' ? t('speaking_phase') : ''}
                  </div>
                  <div className="countdown-display">
                    <span className="countdown-number">{timer}</span>
                    <span className="countdown-unit">{t('seconds')}</span>
                  </div>
                  <div className="phase-tips">
                    {gamePhase === 'prep' ? 
                      t('prepare_topic_tip') : 
                      gamePhase === 'practice' ? 
                      t('start_answering_tip') : ''}
                  </div>
                  
                  {/* 录音状态指示器和转录显示 - 仅在practice阶段显示 */}
                  {gamePhase === 'practice' && (
                    <>
                      <div className="part2-recording-status">
                        <div className="recording-indicator">
                          <div className="recording-wave"></div>
                          <div className="recording-wave"></div>
                          <div className="recording-wave"></div>
                          <div className="recording-wave"></div>
                          <div className="recording-wave"></div>
                        </div>
                        <span className="recording-text">{t('recording_status')}</span>
                      </div>
                      {isRecording && (
                        <div className="transcription-container">
                          <div className="transcription-content">
                            {transcription}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="right-panel">
          <div className="chat-header">
            <div className="chat-title">{t('practice_conversation')}</div>
          </div>
          <div className="chat-messages">
            {part2Messages.map((message, index) => (
              <div key={index} className={`message ${message.type}`}>
                <div className="avatar-container">
                  <div className="user-name">{message.type === 'mentor' ? t('assistant') : (username || t('user'))}</div>
                  <div className={`avatar ${message.type}`}>
                    {message.type === 'mentor' ? t('assistant')[0] : (username ? username[0] : t('user')[0])}
                  </div>
                </div>
                <div className="message-wrapper">
                  <div className="message-content">
                    {message.isVoice ? (
                      // 语音消息的特殊渲染，添加控制按钮
                      <div className="voice-message-container">
                        <div className="voice-message" onClick={() => playVoiceMessage(message)}>
                          <span className="voice-icon">🔊</span>
                          <div className="voice-waveform">
                            <div className="wave"></div>
                            <div className="wave"></div>
                            <div className="wave"></div>
                            <div className="wave"></div>
                          </div>
                        </div>
                        {message.controls && message.controls.canShowText && (
                          <button 
                            onClick={() => showOriginalText(message)}
                            className="show-text-button"
                          >
                            {t('view_text')}
                          </button>
                        )}
                      </div>
                    ) : (
                      message.content
                    )}
                  </div>
                  <div className="message-time">{message.time}</div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        </div>
      </div>
    </>
  );
};

export default Part2Training; 